import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import Pagination from '@/components/ui/Pagination'
import RefreshBar from '@/components/ui/RefreshBar'
import SearchInput from '@/components/ui/SearchInput'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { useLastClose } from '@/features/market/lib/useLastClose'
import { pricesKeys } from '@/features/prices/api/keys'
import { useStockPrices } from '@/features/prices/api/queries'
import PriceTable from '@/features/prices/components/PriceTable'
import { useManualRefresh } from '@/hooks/useManualRefresh'
import { useOnline } from '@/hooks/useOnline'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import { loadStockPrices, toStockPricesQuery } from '@/screens/market/StockPricesScreen.loader'
import { stockPricesQuerySchema, type StockPricesPage } from '@/schemas/prices'
import type { PageSize } from '@/schemas/table'

const HEADER = <TitleRow title="Stock prices" />

/** The mock stacks the strip, the search and the table 16 apart, not the page's 24. */
const STACK = 'gap-4'

/** The mock's search is a raised 44 field, taller and lighter than a toolbar's 36. */
const SEARCH = 'bg-popover lg:h-11'

/**
 * The loaded page, block for block: the search field (44 below `lg`, 36 from
 * it — `SearchInput`'s own), then the two-line table (`h-table-head` band, ten
 * `h-table-row-two-line` rows) with `Pagination`'s own `mt-6` and 44 / 32 foot.
 */
function StockPricesPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading stock prices…"
      header={HEADER}
      pageTitle="Stock prices"
      mainClassName={STACK}
    >
      <Skeleton className="h-11 w-full" />
      <div>
        <DataTableSkeleton rows={10} columns={5} rowHeight="lg" />
        <div className="mt-6 h-11 lg:h-8" />
      </div>
    </ScreenLayout>
  )
}

function StockPricesError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load stock prices"
      description="The data did not come back. Nothing on your device is wrong."
    />
  )
}

export const Route = createFileRoute('/_app/market/stock-prices')({
  validateSearch: stockPricesQuerySchema,
  loader: loadStockPrices,
  pendingComponent: StockPricesPending,
  errorComponent: StockPricesError,
  component: StockPricesScreen,
})

function StockPricesScreen() {
  // Re-parsed through the loader's own schema rather than read straight off
  // `Route.useSearch()`: the router merges the URL's raw query string into
  // that object, so an unrecognised param (`?utm_source=...`) would otherwise
  // key `useStockPrices` here off a different object than the one the loader
  // warmed for the entry URL — see `StockPricesScreen.loader.ts`'s own
  // comment on `toStockPricesQuery`.
  const search = toStockPricesQuery(Route.useSearch())
  const navigate = Route.useNavigate()
  const online = useOnline()
  const query = useStockPrices(search)
  const manual = useManualRefresh([query])
  const [term, setTerm] = useState(search.query ?? '')

  // Hand-rolled debounce, not a new dependency: 300ms after the last
  // keystroke the term lands in the URL as `query` and the page resets to 1.
  // `navigate` is stable across renders, so this only re-arms on a keystroke.
  //
  // Guarded by comparing against the last `query` actually committed to the
  // URL (see CompaniesScreen.tsx's own comment on this pattern) rather than
  // firing unconditionally: without it, this effect's normal mount run —
  // `term` "changing" from nothing to its initial value, same as every
  // effect — fired `navigate` once on every load and smuggled an unprompted
  // `page: 1` into the URL of a page nobody had typed into yet. React 18
  // <StrictMode> (src/main.tsx) makes that worse in dev by running the
  // effect twice on mount, but the bug was already live in production: the
  // navigate itself was a same-value no-op there, silent but still wrong.
  const lastCommittedQuery = useRef(search.query)
  useEffect(() => {
    const next = term || undefined
    if (next === lastCommittedQuery.current) return
    const id = setTimeout(() => {
      lastCommittedQuery.current = next
      void navigate({ search: (prev) => ({ ...prev, query: next, page: 1 }) })
    }, 300)
    return () => {
      clearTimeout(id)
    }
  }, [term, navigate])

  const lastClose = useLastClose<StockPricesPage>(pricesKeys.stockPriceTables(), query.data)
  const data = lastClose.data
  const view = lastClose.isShowingSaved ? 'loaded' : resolveViewState(query, { online })

  return (
    <ScreenLayout header={HEADER} pageTitle="Stock prices" mainClassName={STACK}>
      {lastClose.isShowingSaved ? <RefreshBar state="offline" onRetry={manual.refresh} /> : null}

      <SearchInput
        aria-label="Symbol or company"
        placeholder="Symbol or company"
        className={SEARCH}
        value={term}
        onChange={(event) => {
          setTerm(event.target.value)
        }}
      />

      {view === 'failed' || view === 'offline' ? (
        <EmptyState
          kind={view}
          size="page"
          title={view === 'offline' ? undefined : "Couldn't load stock prices"}
          onRetry={manual.refresh}
          retrying={manual.isManual}
          meta={view === 'failed' ? lastUpdatedMeta(query.dataUpdatedAt) : undefined}
          savedAt={lastClose.savedAt}
          onShowLastClose={lastClose.showLastClose}
        />
      ) : (
        <>
          {/* Table and pager travel as ONE block of the stack: `Pagination` draws
              its own 24px (see its PLACEMENT note) and a parent `gap-*` would add
              to it rather than absorb it. */}
          <div>
            <PriceTable
              rows={data?.rows ?? []}
              variant="prices"
              isLoading={query.isLoading}
              pageSize={search.pageSize}
              sortBy={search.sortBy}
              sortDesc={search.sortDesc}
              onSortChange={(sortBy, sortDesc) => {
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    sortBy,
                    sortDesc: sortBy === undefined ? undefined : sortDesc,
                    page: 1,
                  }),
                })
              }}
            />

            <Pagination
              page={data?.page ?? search.page}
              pageSize={data?.pageSize ?? search.pageSize}
              total={data?.total ?? 0}
              // `/market/live/` at `LIVE_MAX_LIMIT` serves every company that
              // trades, so `total` is the market, not a window of it.
              unit="companies"
              onPageChange={(page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              }}
              onPageSizeChange={(pageSize) => {
                void navigate({
                  search: (prev) => ({ ...prev, pageSize: pageSize as PageSize, page: 1 }),
                })
              }}
            />
          </div>
        </>
      )}
    </ScreenLayout>
  )
}
